export const ORACLE_SELF_AUDIT_SCHEMA_VERSION = 'HELIX_ORACLE_SELF_AUDIT_SUMMARY_V1' as const;

export type OracleSelfAuditStatus = 'pass' | 'finding' | 'error';

export type MustSumMetricSelector = Readonly<{
  contextKind: string;
  schemaVersion: string;
  metricKey: string;
  valuePath: readonly string[];
  cohortPath: readonly string[];
}>;

export type MustSumComponentSelector = MustSumMetricSelector &
  Readonly<{
    componentId: string;
  }>;

export type MustSumInvariantContract = Readonly<{
  contractId: string;
  moduleId: string;
  scopeKind: 'tenant_total';
  parent: MustSumMetricSelector;
  components: readonly MustSumComponentSelector[];
  residual: MustSumMetricSelector &
    Readonly<{
      residualId: string;
      classification: 'unallocated_non_addressable';
      classificationPath: readonly string[];
      includedInOpportunity: false;
      includedInOpportunityPath: readonly string[];
      bindingMode: 'explicit_unclassified_residual';
      bindingModePath: readonly string[];
      financeArtifactIdPath: readonly string[];
      financeSourceRefPath: readonly string[];
      componentBindingsPath: readonly string[];
    }>;
  cohortValueKind: 'fiscal_year';
  tolerance: number;
  toleranceKind: 'absolute';
}>;

export const MUST_SUM_INVARIANT_REGISTRY = [
  {
    contractId: 'finance_sga_usd_to_governed_lever_pools_v1',
    moduleId: 'finance',
    scopeKind: 'tenant_total',
    parent: {
      contextKind: 'decision_agent:finance:governed_pnl_snapshot',
      schemaVersion: 'finance_pnl_governed_snapshot_v1',
      metricKey: 'sga_usd',
      valuePath: ['state', 'signals', 'operating_expense_usd'],
      cohortPath: ['state', 'asOf'],
    },
    components: [
      {
        componentId: 'professional_fees',
        contextKind: 'decision_agent:professional_fees:governed_snapshot',
        schemaVersion: 'professional_fees_governed_snapshot_v1',
        metricKey: 'total_spend_usd',
        valuePath: ['signals', 'total_spend_usd'],
        cohortPath: ['reportingPeriod'],
      },
      {
        componentId: 'outside_contractors',
        contextKind: 'decision_agent:outside_contractors:governed_snapshot',
        schemaVersion: 'outside_contractors_governed_snapshot_v1',
        metricKey: 'total_spend_usd',
        valuePath: ['signals', 'total_spend_usd'],
        cohortPath: ['reportingPeriod'],
      },
      {
        componentId: 'indirect_procurement',
        contextKind: 'decision_agent:indirect_procurement:governed_snapshot',
        schemaVersion: 'indirect_procurement_governed_snapshot_v1',
        metricKey: 'total_indirect_spend_usd',
        valuePath: ['signals', 'total_indirect_spend_usd'],
        cohortPath: ['reportingPeriod'],
      },
      {
        componentId: 'travel_and_expense',
        contextKind: 'decision_agent:travel_expense:governed_snapshot',
        schemaVersion: 'travel_expense_governed_snapshot_v1',
        metricKey: 'total_tne_spend_usd',
        valuePath: ['signals', 'total_tne_spend_usd'],
        cohortPath: ['reportingPeriod'],
      },
      {
        componentId: 'shared_services',
        contextKind: 'decision_agent:shared_services:governed_snapshot',
        schemaVersion: 'shared_services_governed_snapshot_v1',
        metricKey: 'total_spend_usd',
        valuePath: ['signals', 'total_spend_usd'],
        cohortPath: ['reportingPeriod'],
      },
      {
        componentId: 'tech_software',
        contextKind: 'decision_agent:tech_software:governed_snapshot',
        schemaVersion: 'tech_software_governed_snapshot_v1',
        metricKey: 'total_spend_usd',
        valuePath: ['signals', 'total_spend_usd'],
        cohortPath: ['reportingPeriod'],
      },
      {
        componentId: 'real_estate_footprint',
        contextKind: 'decision_agent:real_estate_footprint:governed_snapshot',
        schemaVersion: 'real_estate_footprint_governed_snapshot_v1',
        metricKey: 'total_spend_usd',
        valuePath: ['signals', 'total_spend_usd'],
        cohortPath: ['reportingPeriod'],
      },
      {
        componentId: 'marketing_spend',
        contextKind: 'decision_agent:marketing_spend:governed_snapshot',
        schemaVersion: 'marketing_spend_governed_snapshot_v1',
        metricKey: 'total_spend_usd',
        valuePath: ['signals', 'total_spend_usd'],
        cohortPath: ['reportingPeriod'],
      },
    ],
    residual: {
      residualId: 'unclassified_sga_residual_spend_usd',
      contextKind: 'decision_agent:finance:sga_line_reconciliation',
      schemaVersion: 'finance_pnl_governed_snapshot_v1',
      metricKey: 'unclassified_sga_residual_spend_usd',
      valuePath: ['unclassifiedResidualSpendUsd'],
      cohortPath: ['sgaReconciliation', 'sourceAsOf'],
      classification: 'unallocated_non_addressable',
      classificationPath: ['sgaReconciliation', 'classification'],
      includedInOpportunity: false,
      includedInOpportunityPath: ['sgaReconciliation', 'includedInOpportunity'],
      bindingMode: 'explicit_unclassified_residual',
      bindingModePath: ['sgaReconciliation', 'bindingMode'],
      financeArtifactIdPath: ['sgaReconciliation', 'financeArtifactId'],
      financeSourceRefPath: ['sgaReconciliation', 'financeSourceRef'],
      componentBindingsPath: ['sgaReconciliation', 'componentBindings'],
    },
    cohortValueKind: 'fiscal_year',
    tolerance: 0.005,
    toleranceKind: 'absolute',
  },
] as const satisfies readonly MustSumInvariantContract[];

export type ApprovedArtifactEvidenceBinding = Readonly<{
  bindingId: string;
  contextKind: string;
  schemaVersion: string;
  evidenceKey: string;
  evidenceKeyPath: readonly string[];
}>;

export const APPROVED_ARTIFACT_EVIDENCE_BINDING_REGISTRY = [
  {
    bindingId: 'finance_sga_line_reconciliation_v1',
    contextKind: 'decision_agent:finance:sga_line_reconciliation',
    schemaVersion: 'finance_pnl_governed_snapshot_v1',
    evidenceKey: 'finance_sga_line_reconciliation',
    evidenceKeyPath: ['sgaReconciliation', 'evidenceKey'],
  },
] as const satisfies readonly ApprovedArtifactEvidenceBinding[];

export type ControlTotalRowExpression =
  | Readonly<{
      kind: 'sum';
      field: string;
    }>
  | Readonly<{
      kind: 'sum_difference';
      minuendField: string;
      subtrahendField: string;
    }>;

export type ControlTotalAdmissionBinding = Readonly<{
  aggregateKey: string;
  rowExpression: ControlTotalRowExpression;
  allowPopulationExtrapolation: boolean;
  populationExtrapolationLabelPath?: readonly string[];
}>;

export type ControlTotalAdmissionContract = Readonly<{
  contractId: string;
  moduleId: string;
  contextKind: string;
  schemaVersion: string;
  rowsPath: readonly string[];
  controlTotalsPath: readonly string[];
  tolerance: number;
  toleranceKind: 'relative';
  bindings: readonly ControlTotalAdmissionBinding[];
}>;

export const CONTROL_TOTAL_ADMISSION_REGISTRY = [
  {
    contractId: 'pricing_discount_transaction_control_totals_v1',
    moduleId: 'pricing-realization',
    contextKind: 'decision_agent:pricing_realization:discount_transaction_snapshot',
    schemaVersion: 'pricing_discount_transaction_snapshot_v1',
    rowsPath: ['content', 'transactions'],
    controlTotalsPath: ['content', 'controlTotals'],
    tolerance: 0.005,
    toleranceKind: 'relative',
    bindings: [
      {
        aggregateKey: 'pricingRevenueEnvelopeUsd',
        rowExpression: { kind: 'sum', field: 'listRevenueUsd' },
        allowPopulationExtrapolation: true,
        populationExtrapolationLabelPath: [
          'content',
          'controlTotalLabels',
          'pricingRevenueEnvelopeUsd',
        ],
      },
      {
        aggregateKey: 'totalDiscountUsd',
        rowExpression: { kind: 'sum', field: 'discountUsd' },
        allowPopulationExtrapolation: false,
      },
      {
        aggregateKey: 'totalListRevenueUsd',
        rowExpression: { kind: 'sum', field: 'listRevenueUsd' },
        allowPopulationExtrapolation: false,
      },
      {
        aggregateKey: 'totalNetRevenueUsd',
        rowExpression: { kind: 'sum', field: 'netRevenueUsd' },
        allowPopulationExtrapolation: false,
      },
      {
        aggregateKey: 'totalOffPolicyLeakageUsd',
        rowExpression: { kind: 'sum', field: 'offPolicyLeakageUsd' },
        allowPopulationExtrapolation: false,
      },
      {
        aggregateKey: 'totalWithinPolicyDiscountUsd',
        rowExpression: {
          kind: 'sum_difference',
          minuendField: 'discountUsd',
          subtrahendField: 'offPolicyLeakageUsd',
        },
        allowPopulationExtrapolation: false,
      },
    ],
  },
] as const satisfies readonly ControlTotalAdmissionContract[];

export type OracleSelfAuditFinding = Readonly<{
  code: string;
  guardId: string;
  [detail: string]: unknown;
}>;

export type OracleSelfAuditError = Readonly<{
  code: string;
  [detail: string]: unknown;
}>;

export type OracleSelfAuditGuardSummary<
  TFinding extends OracleSelfAuditFinding = OracleSelfAuditFinding,
> = Readonly<{
  guardId: string;
  status: OracleSelfAuditStatus;
  evaluatedCount: number;
  findingCount: number;
  errorCount?: number;
  findings: readonly TFinding[];
  errors?: readonly OracleSelfAuditError[];
  [detail: string]: unknown;
}>;

export type OracleSelfAuditSummary = Readonly<{
  schemaVersion: typeof ORACLE_SELF_AUDIT_SCHEMA_VERSION;
  runId: string;
  generatedAt: string;
  tenantId: string;
  databaseName: string;
  readOnly: true;
  oracleRecipeCount: number;
  overallStatus: OracleSelfAuditStatus;
  findingCount: number;
  errorCount: number;
  guards: Readonly<Record<string, OracleSelfAuditGuardSummary>>;
}>;
